import { Controller } from "@hotwired/stimulus"

// Handles adding/removing a single log from an investigation, from the log detail modal.
export default class extends Controller {
  static targets = ["popover", "list", "status"]
  static values = { investigationsUrl: String, logUuid: String }

  #open = false
  #boundOutside = null

  connect() {
    this.#boundOutside = e => {
      if (this.#open && !this.element.contains(e.target)) this.#close()
    }
    document.addEventListener("click", this.#boundOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.#boundOutside)
  }

  toggle() {
    this.#open ? this.#close() : this.#openPopover()
  }

  async addToInvestigation(event) {
    const investigationId = event.currentTarget.dataset.investigationId
    if (!investigationId) return

    const annotation = this.hasListTarget
      ? (this.listTarget.querySelector(`input.siem-log-inv-annotation[data-investigation-id="${investigationId}"]`)?.value?.trim() || "")
      : ""

    const url = `${this.investigationsUrlValue}/${investigationId}/items`
    const body = new URLSearchParams({
      log_uuid: this.logUuidValue,
      annotation,
      authenticity_token: document.querySelector('meta[name="csrf-token"]')?.content || "",
    })

    try {
      const resp = await fetch(url, {
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        },
      })
      if (resp.ok) {
        this.#showStatus("Added!")
        this.#close()
      } else {
        const data = await resp.json()
        this.#showStatus(data.errors?.join(", ") || "Failed to add")
      }
    } catch {
      this.#showStatus("Request failed")
    }
  }

  async #openPopover() {
    this.#open = true
    this.popoverTarget.classList.remove("hidden")

    try {
      const resp = await fetch(this.investigationsUrlValue, { headers: { Accept: "application/json" } })
      const investigations = await resp.json()
      this.#renderList(investigations)
    } catch {
      this.#showStatus("Failed to load investigations")
    }
  }

  #close() {
    this.#open = false
    this.popoverTarget.classList.add("hidden")
  }

  #renderList(investigations) {
    if (!this.hasListTarget) return
    if (investigations.length === 0) {
      this.listTarget.innerHTML = '<p class="siem-log-inv-empty">No investigations. Create one via the Notebook.</p>'
      return
    }
    this.listTarget.innerHTML = investigations.map(inv => `
      <div class="siem-log-inv-row">
        <button class="siem-log-inv-option"
                type="button"
                data-action="click->accounts--logs--log-investigation#addToInvestigation"
                data-investigation-id="${inv.id}">
          <span class="siem-log-inv-name">${this.#esc(inv.name)}</span>
          <span class="siem-log-inv-count">${inv.log_count} log${inv.log_count !== 1 ? "s" : ""}</span>
        </button>
        <input class="form-control input-sm siem-log-inv-annotation"
               type="text"
               placeholder="Note (optional)…"
               data-investigation-id="${inv.id}" />
      </div>`).join("")
  }

  #showStatus(msg) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = msg
      setTimeout(() => { if (this.hasStatusTarget) this.statusTarget.textContent = "" }, 3000)
    }
  }

  #esc(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
